<script setup lang="ts">
import type { Sign as SignInterface } from '@/models/sign'
import { $signOptions } from '@/api/signs'
import type {
  ReprintArtworkTypeOption,
  ReprintCostTypeOption,
  ReprintPatchPriceTypeOption,
} from '@/models/wayfinding/sign-reprint'
import { capitalizeFirstLetters } from '@/helpers/utils/capitalizeFirstLetters'

interface IProps {
  sign?: SignInterface,
}

const props = withDefaults(defineProps<IProps>(), {
  sign: () => ({}),
})

interface Emit {
  (e: 'update:sign', value: SignInterface): void
}

const emit = defineEmits<Emit>()

const loading = ref<boolean>(false)

const reprintCostTypeOptions = ref<ReprintCostTypeOption[]>([])
const reprintArtworkTypeOptions = ref<ReprintArtworkTypeOption[]>([])
const reprintPatchPriceTypeOptions = ref<ReprintPatchPriceTypeOption[]>([])

const computedSign = computed({
  // getter
  get: () => props.sign || {},  // Provide a fallback in case props.sign is undefined
  // setter
  set: (newSign: SignInterface) => {
    emit('update:sign', newSign) // Emit update to parent when the local sign is changed
  }
})

onMounted(() => {
  getSignReprintOptions()
})

const getSignReprintOptions = async () => {
  loading.value = true

  try {
    const res = await $signOptions()
    reprintCostTypeOptions.value = res.data.actions.POST.reprint_cost_type.choices
    reprintArtworkTypeOptions.value = res.data.actions.POST.reprint_artwork_type.choices
    reprintPatchPriceTypeOptions.value = res.data.actions.POST.reprint_patch_price_type.choices
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <VCardText>
    <VRow>
      <VCol>
        <span>Reprint Type:
          <span class="text-h6 font-weight-bold">
            {{ capitalizeFirstLetters(computedSign?.reprint_type_display) }}
          </span>
        </span>
      </VCol>
    </VRow>
    <VRow>
      <VCol>
        <VBtnToggle
          v-model="computedSign.reprint_cost_type"
          class="d-flex"
          color="primary"
          divided
          mandatory
          variant="outlined"
        >
          <VBtn
            v-for="item in reprintCostTypeOptions"
            :key="item.value"
            :value="item.value"
            class="flex-grow-1"
          >
            {{ item.display_name }}
          </VBtn>
        </VBtnToggle>
      </VCol>
    </VRow>
    <VRow>
      <VCol
        :cols="12"
      >
        <VTextarea
          v-model="computedSign.reprint_comment"
          label="Reprint Comment"
          rows="3"
        />
      </VCol>
    </VRow>
    <!-- If Full Reprint -->
    <VRow v-if="computedSign?.reprint_type === 1">
      <VCol>
        <VBtnToggle
          v-model="computedSign.reprint_artwork_type"
          class="d-flex"
          color="primary"
          divided
          mandatory
          variant="outlined"
        >
          <VBtn
            v-for="item in reprintArtworkTypeOptions"
            :key="item.value"
            :value="item.value"
            class="flex-grow-1"
          >
            {{ item.display_name }}
          </VBtn>
        </VBtnToggle>
      </VCol>
    </VRow>
    <!-- /If Full Reprint -->
    <!-- If Patch Reprint -->
    <VRow v-if="computedSign?.reprint_type === 2">
      <VCol>
        <VBtnToggle
          v-model="computedSign.reprint_patch_price_type"
          class="d-flex"
          color="primary"
          divided
          mandatory
          variant="outlined"
        >
          <VBtn
            v-for="item in reprintPatchPriceTypeOptions"
            :key="item.value"
            :value="item.value"
            class="flex-grow-1"
          >
            {{ item.display_name }}
          </VBtn>
        </VBtnToggle>
      </VCol>
    </VRow>
    <!-- /If Patch Reprint -->
  </VCardText>
</template>

<style lang="scss" scoped>
</style>
